import { Button, HStack, Text } from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import {
  UtilizationReportDataSource,
  UtilizationReportGroupBy,
  type UtilizationReportGroupedByQuery,
  UtilizationReportGroupOrderByColumn,
} from "@src/__generated__/urql-graphql";
import type { UtilizationReportStore } from "@src/components/modules/reports/utilization/stores/utilization-report";
import {
  AvailableCapacityTooltip,
  TrackedAndAllocatedTooltip,
  userCell,
} from "@src/components/modules/reports/utilization/UtilizationReport";
import { ACTIONS_COLUMN_ID, type BodyColumnDef } from "@src/components/ui-kit";
import { RangeProgressBar } from "@src/components/ui-kit/ProgressBar/RangeProgressBar";
import { currency } from "@src/utils/formatters";
import { useStore } from "@src/utils/hooks";
import { formatSecondsToHours } from "@src/utils/time";
import { differenceInMonths } from "date-fns";
import { computed } from "mobx";
import { now } from "mobx-utils";
import { useMemo } from "react";

type UtilizationReportGroupedByData =
  UtilizationReportGroupedByQuery["utilizationReportGroups"]["data"][0];

export type UtilizationGroupedTableRow =
  | {
      id: string;
      rowType: "group";
      group_title: UtilizationReportGroupedByData["group_title"];
      report: UtilizationReportGroupedByData["report"];
      items: UtilizationReportGroupedByData["items"];
      __subRows?: UtilizationGroupedTableRow[];
    }
  | {
      id: string;
      rowType: "item";
      group_title: UtilizationReportGroupedByData["group_title"];
      report: UtilizationReportGroupedByData["items"][0]["report"];
      user: UtilizationReportGroupedByData["items"][0]["user"];
      items: UtilizationReportGroupedByData["items"];
    };

const isGroupRow = (
  row: UtilizationGroupedTableRow,
): row is Extract<UtilizationGroupedTableRow, { rowType: "group" }> =>
  row.rowType === "group";

export function useUtilizationGroupedTableCols(store: UtilizationReportStore) {
  const { workspaceStore } = useStore();

  const cols = useMemo<
    | BodyColumnDef<
        UtilizationGroupedTableRow,
        UtilizationReportGroupOrderByColumn
      >[]
    | undefined
  >(() => {
    const isDateInPast = computed(() => {
      return differenceInMonths(store.dateRange.start, now()) < 0;
    }).get();

    const positionCol: BodyColumnDef<
      UtilizationGroupedTableRow,
      UtilizationReportGroupOrderByColumn
    > = {
      id: "position",
      meta: { label: t`Position` },
      header: () => t`Position`,
      cell: ({ row }) =>
        isGroupRow(row.original) ? null : (
          <span>{row.original.user.profile?.defaultWorktype.title}</span>
        ),
    };

    const teamCol: BodyColumnDef<
      UtilizationGroupedTableRow,
      UtilizationReportGroupOrderByColumn
    > = {
      id: "team",
      meta: { label: t`Team` },
      header: () => t`Team`,
      cell: ({ row }) =>
        isGroupRow(row.original) ? null : (
          <span>{row.original.user.team?.name}</span>
        ),
    };

    const sourceColVariants: Record<
      UtilizationReportDataSource,
      BodyColumnDef<
        UtilizationGroupedTableRow,
        UtilizationReportGroupOrderByColumn
      >
    > = {
      [UtilizationReportDataSource.TimeTracking]: {
        id: "tracked",
        meta: { label: t`Tracked` },
        header: () => t`Tracked`,
        orderBy: UtilizationReportGroupOrderByColumn.TrackedTime,
        cell: ({ row }) => (
          <span>
            {formatSecondsToHours(row.original.report.tracked_time ?? 0, 1)}
          </span>
        ),
      },
      [UtilizationReportDataSource.Planning]: {
        id: "allocated",
        meta: { label: t`Allocated` },
        header: () => t`Allocated`,
        orderBy: UtilizationReportGroupOrderByColumn.AllocatedTime,
        cell: ({ row }) => (
          <span>
            {formatSecondsToHours(row.original.report.allocated_time ?? 0, 1)}
          </span>
        ),
      },
      [UtilizationReportDataSource.TaskPositions]: {
        id: "task-budgeted",
        meta: { label: t`Task budgeted` },
        header: () => t`Task budgeted`,
        orderBy: UtilizationReportGroupOrderByColumn.BudgetedTime,
        cell: ({ row }) => (
          <span>
            {formatSecondsToHours(row.original.report.budgeted_time ?? 0, 1)}
          </span>
        ),
      },
      [UtilizationReportDataSource.Combined]: {
        id: "tracked+allocated",
        meta: { label: t`Tracked + Allocated` },
        header: () => t`Tracked + Allocated`,
        orderBy: UtilizationReportGroupOrderByColumn.CombinedTiMe,
        cell: ({ row }) => (
          <TrackedAndAllocatedTooltip
            row={row.original}
            date={store.dateRange.start}
          >
            <span>
              {formatSecondsToHours(row.original.report.combined_time ?? 0, 1)}
            </span>
          </TrackedAndAllocatedTooltip>
        ),
      },
    };

    const sourceCol = sourceColVariants[store.dataSource];
    const billableCol: BodyColumnDef<
      UtilizationGroupedTableRow,
      UtilizationReportGroupOrderByColumn
    > = {
      id: "billable",
      meta: { label: t`Billable` },
      header: () => t`Billable`,
    };

    switch (store.dataSource) {
      case UtilizationReportDataSource.TimeTracking:
        billableCol.orderBy =
          UtilizationReportGroupOrderByColumn.TrackedTimeBillable;
        billableCol.cell = ({ row }) => (
          <span>
            {formatSecondsToHours(
              row.original.report.tracked_time_billable ?? 0,
              1,
            )}
          </span>
        );
        break;
      case UtilizationReportDataSource.Planning:
        billableCol.orderBy =
          UtilizationReportGroupOrderByColumn.AllocatedTimeBillable;
        billableCol.cell = ({ row }) => (
          <span>
            {formatSecondsToHours(
              row.original.report.allocated_time_billable ?? 0,
              1,
            )}
          </span>
        );
        break;
      case UtilizationReportDataSource.TaskPositions:
        billableCol.orderBy =
          UtilizationReportGroupOrderByColumn.BudgetedTimeBillable;
        billableCol.cell = ({ row }) => (
          <span>
            {formatSecondsToHours(
              row.original.report.budgeted_time_billable ?? 0,
              1,
            )}
          </span>
        );
        break;
      case UtilizationReportDataSource.Combined:
        billableCol.orderBy =
          UtilizationReportGroupOrderByColumn.CombinedTimeBillable;
        billableCol.cell = ({ row }) => (
          <span>
            {formatSecondsToHours(
              row.original.report.combined_time_billable ?? 0,
              1,
            )}
          </span>
        );
        break;
    }

    const baseColumns: BodyColumnDef<
      UtilizationGroupedTableRow,
      UtilizationReportGroupOrderByColumn
    >[] = [
      {
        id: "grouped-by",
        meta: { label: String(store.activeGroupByOption?.label ?? "") },
        header: () => store.activeGroupByOption?.label,
        orderBy: UtilizationReportGroupOrderByColumn.Group,
        enableHiding: false,
        cell: ({ row }) => {
          if (isGroupRow(row.original)) {
            return <span>{row.original.group_title}</span>;
          }

          const { user } = row.original;

          return userCell(user, () =>
            store.userModalStore.modalState.onOpen({
              id: user.id,
              dataSource: store.dataSource,
              dateRange: store.dateRange,
            }),
          );
        },
      },
      {
        id: "available-capacity",
        meta: { label: t`Available Capacity` },
        header: () => t`Available Capacity`,
        orderBy: UtilizationReportGroupOrderByColumn.AvailableTime,
        cell: ({ row }) => (
          <AvailableCapacityTooltip row={row.original}>
            <span>
              {formatSecondsToHours(row.original.report.available_time, 1)}
            </span>
          </AvailableCapacityTooltip>
        ),
      },
      sourceCol,
      {
        id: "tracked-time",
        meta: { label: t`Tracked time` },
        header: () => t`Tracked time`,
        cell: ({ row }) =>
          formatSecondsToHours(row.original.report.tracked_time ?? 0, 1),
      },
      {
        id: "allocated-time",
        meta: { label: t`Allocated time` },
        header: () => t`Allocated time`,
        cell: ({ row }) =>
          formatSecondsToHours(row.original.report.allocated_time ?? 0, 1),
      },
      {
        id: "left-to-allocate",
        meta: { label: isDateInPast ? t`Unused hours` : t`Left to allocate` },
        header: () => (isDateInPast ? t`Unused hours` : t`Left to allocate`),
        cell: ({ row }) =>
          row.original.report.remaining_time > 0 ? (
            <span>
              {formatSecondsToHours(row.original.report.remaining_time, 1)}
            </span>
          ) : null,
      },
      {
        id: "overtime",
        meta: { label: t`Overtime` },
        header: () => t`Overtime`,
        cell: ({ row }) =>
          row.original.report.overtime > 0 ? (
            <span>{formatSecondsToHours(row.original.report.overtime, 1)}</span>
          ) : null,
      },
      {
        id: "revenue-estimate",
        meta: { label: t`Revenue estimate` },
        header: () => t`Revenue estimate`,
        cell: ({ row }) =>
          currency.formatByWorkspace(
            store.dataSource === UtilizationReportDataSource.Combined
              ? (row.original.report.combined_amount_billable ?? 0)
              : (row.original.report.budgeted_amount_billable ?? 0),
          ),
      },
      billableCol,
      {
        id: "utilization",
        meta: { label: t`Utilization` },
        header: () => t`Utilization`,
        orderBy: UtilizationReportGroupOrderByColumn.UtilizationPercentage,
        cell: ({ row }) => (
          <HStack>
            <RangeProgressBar
              w="96px"
              progress={row.original.report.utilization_percentage ?? 0}
              exceededThreshold={{
                below: isGroupRow(row.original)
                  ? (workspaceStore.settings?.default_utilization_range_from ??
                    80)
                  : (row.original.user.profile?.utilization_range_from ?? 0),
                above: isGroupRow(row.original)
                  ? (workspaceStore.settings?.default_utilization_range_to ??
                    100)
                  : (row.original.user.profile?.utilization_range_to ?? 0),
              }}
            />
            <Text minW="10" textAlign="end">
              {row.original.report.utilization_percentage ?? 0}%
            </Text>
          </HStack>
        ),
      },
      {
        id: "billable-utilization",
        meta: { label: t`Billable utilization` },
        header: () => t`Billable utilization`,
        orderBy:
          UtilizationReportGroupOrderByColumn.UtilizationPercentageBillable,
        cell: ({ row }) => (
          <HStack>
            <RangeProgressBar
              w="96px"
              progress={
                row.original.report.utilization_percentage_billable ?? 0
              }
              exceededThreshold={{
                below: isGroupRow(row.original)
                  ? (workspaceStore.settings?.default_utilization_range_from ??
                    80)
                  : (row.original.user.profile?.utilization_range_from ?? 0),
                above: isGroupRow(row.original)
                  ? (workspaceStore.settings?.default_utilization_range_to ??
                    100)
                  : (row.original.user.profile?.utilization_range_to ?? 0),
              }}
            />
            <Text minW="10" textAlign="end">
              {row.original.report.utilization_percentage_billable ?? 0}%
            </Text>
          </HStack>
        ),
      },
      {
        id: ACTIONS_COLUMN_ID,
        meta: { label: t`Actions` },
        header: () => t`Actions`,
        cell: ({ row }) => {
          if (isGroupRow(row.original)) {
            return null;
          }

          const { user } = row.original;

          return (
            <Button
              fontWeight="medium"
              onClick={() => {
                store.userModalStore.modalState.onOpen({
                  id: user.id,
                  dataSource: store.dataSource,
                  dateRange: store.dateRange,
                });
              }}
              role="link"
              size="xs"
              variant="link"
            >
              <Trans>Detail</Trans>
            </Button>
          );
        },
      },
    ];

    switch (store.groupBy) {
      case UtilizationReportGroupBy.Month:
      case UtilizationReportGroupBy.Week:
        return [
          ...baseColumns.slice(0, 1),
          teamCol,
          positionCol,
          ...baseColumns.slice(1),
        ];
      case UtilizationReportGroupBy.WorkType:
        return [...baseColumns.slice(0, 1), teamCol, ...baseColumns.slice(1)];
      case UtilizationReportGroupBy.Team:
        return [
          ...baseColumns.slice(0, 1),
          positionCol,
          ...baseColumns.slice(1),
        ];
      default:
        return undefined;
    }
  }, [
    store.activeGroupByOption?.label,
    store.dataSource,
    store.dateRange,
    store.groupBy,
    store.userModalStore.modalState,
    workspaceStore.settings?.default_utilization_range_from,
    workspaceStore.settings?.default_utilization_range_to,
  ]);

  return cols;
}
